import { X, Plus, Euro, AlertTriangle } from 'lucide-react';
import { FormData } from '../../../types/transaction';
import { useMemo, useEffect, useRef } from 'react';
import {
  buildDistributionLineKeys,
  getRowDistributionPercentage,
  checkRetrocessionsAgainstNegotiators
} from '../../../utils/retrocessions';

interface RetrocessionsFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
  readOnly?: boolean;
  showPaymentDates?: boolean;
}

type NegotiatorLine = FormData['negotiators'][number];

/** Libellé d'une ligne : "Bryan RONDEL — SAINT-NAZAIRE" si le nom seul est ambigu */
function buildLineLabels(lines: NegotiatorLine[]): string[] {
  return lines.map(line => {
    const isAmbiguous = lines.filter(other => other.id === line.id).length > 1;
    return isAmbiguous && line.agency ? `${line.name} — ${line.agency}` : line.name;
  });
}

/** Répartition proportionnelle par défaut, une entrée par ligne de négociateur */
function buildDefaultPercentages(lines: NegotiatorLine[]): { [key: string]: number; total: number } {
  const keys = buildDistributionLineKeys(lines);
  const totalPercentage = lines.reduce((sum, n) => sum + n.percentage, 0);

  const percentages = keys.reduce((acc, key, i) => {
    acc[key] = totalPercentage > 0 ? (lines[i].percentage / totalPercentage) * 100 : 0;
    return acc;
  }, {} as { [key: string]: number });

  percentages.total = Object.values(percentages).reduce((sum, v) => sum + v, 0);
  return percentages as { [key: string]: number; total: number };
}

export default function RetrocessionsForm({ formData, onChange, readOnly = false, showPaymentDates = false }: RetrocessionsFormProps) {
  // Liste des négociateurs au rendu précédent, pour détecter un remplacement
  const previousSignatureRef = useRef<string | null>(null);

  // Recalculer automatiquement les rétrocessions quand les honoraires changent
  useEffect(() => {
    if (readOnly) return;

    const feesHT = parseFloat(formData.fees_without_tax);
    if (isNaN(feesHT)) return;

    let hasChanges = false;
    const updatedRetrocessions = formData.retrocessions.map(retro => {
      // Ne recalculer que pour les types avec calcul automatique (20% HT)
      if (['recommandation_interne', 'uff', 'retrocession_lefeuvre', 'retrocession_newco', 'retrocession_syndic', 'duo_coaching'].includes(retro.type)) {
        const rawAmount = retro.type === 'duo_coaching' ? (feesHT * 0.2) / 1.2 : feesHT * 0.2;
        const expectedAmount = rawAmount.toFixed(2);
        
        // Si le montant actuel est différent du montant calculé, le mettre à jour
        if (parseFloat(retro.amount).toFixed(2) !== expectedAmount) {
          hasChanges = true;
          return { ...retro, amount: expectedAmount };
        }
      }
      return retro;
    });

    // Mettre à jour seulement si des changements ont été détectés
    if (hasChanges) {
      console.log('[RetrocessionsForm] Auto-recalculating retrocessions due to fees change');
      onChange({ ...formData, retrocessions: updatedRetrocessions });
    }
  }, [formData.fees_without_tax, readOnly]);

  const handleAddRetrocession = () => {
    if (!readOnly && formData.negotiators.length > 0) {
      // S'assurer qu'il y a au moins un négociateur avec un ID valide
      const validNegotiator = formData.negotiators.find(n => n.id);
      if (!validNegotiator) {
        console.warn('Aucun négociateur valide trouvé pour la rétrocession');
        return;
      }

      // Filtrer les négociateurs valides
      const validNegotiators = formData.negotiators.filter(n => n.id);

      // Log des négociateurs valides
      console.log('[RetrocessionsForm] Valid negotiators:', validNegotiators);

      // Répartition proportionnelle, une entrée par ligne de négociateur
      // (un même négociateur peut apparaître deux fois, une fois par agence)
      const percentages = buildDefaultPercentages(validNegotiators);

      const newRetrocession = {
        type: 'champagne',
        amount: '21',
        description: '',
        payment_date: null,
        distribution_type: 'custom',
        primary_negotiator_id: validNegotiator.id,
        secondary_negotiator_id: null,
        tertiary_negotiator_id: null,
        quaternary_negotiator_id: null,
        distribution_percentages: percentages
      };

      // Log de la nouvelle rétrocession
      console.log('[RetrocessionsForm] New retrocession created:', newRetrocession);

      onChange({
        ...formData,
        retrocessions: [...formData.retrocessions, newRetrocession]
      });
    }
  };

  const handleRemoveRetrocession = (index: number) => {
    if (!readOnly) {
      console.log('[RetrocessionsForm] Removing retrocession at index:', index);
      const newRetrocessions = formData.retrocessions.filter((_, i) => i !== index);
      onChange({
        ...formData,
        retrocessions: newRetrocessions
      });
    }
  };

  const handleDistributionChange = (index: number, value: string) => {
    if (!readOnly) {
      const newRetrocessions = [...formData.retrocessions];
      const retrocession = newRetrocessions[index];

      // Filtrer les négociateurs avec des IDs valides
      const validNegotiators = formData.negotiators.filter(n => n.id);
      if (validNegotiators.length === 0) {
        console.warn('Aucun négociateur valide trouvé pour la distribution');
        return;
      }

      console.log('[RetrocessionsForm] Changing distribution:', {
        index,
        value,
        validNegotiators,
        currentRetrocession: retrocession
      });

      // Si la valeur commence par "single-", c'est une sélection de négociateur unique
      if (value.startsWith('single-')) {
        const negotiatorId = value.replace('single-', '');
        if (!negotiatorId) {
          console.warn('ID de négociateur invalide pour la distribution single');
          return;
        }

        retrocession.distribution_type = 'single';
        retrocession.primary_negotiator_id = negotiatorId;
        retrocession.secondary_negotiator_id = null;
        retrocession.tertiary_negotiator_id = null;
        retrocession.quaternary_negotiator_id = null;
        retrocession.distribution_percentages = undefined;

        console.log('[RetrocessionsForm] Updated to single distribution:', {
          negotiatorId,
          updatedRetrocession: retrocession
        });
      } else if (value === 'custom') {
        // Répartition proportionnelle par défaut, une entrée par ligne de négociateur
        const percentages = buildDefaultPercentages(validNegotiators);

        retrocession.distribution_type = 'custom';
        retrocession.primary_negotiator_id = validNegotiators[0].id;
        retrocession.secondary_negotiator_id = validNegotiators[1]?.id || null;
        retrocession.tertiary_negotiator_id = validNegotiators[2]?.id || null;
        retrocession.quaternary_negotiator_id = validNegotiators[3]?.id || null;
        retrocession.distribution_percentages = percentages;

        console.log('[RetrocessionsForm] Updated to custom distribution:', {
          validNegotiators,
          initialPercentages: retrocession.distribution_percentages
        });
      }

      onChange({ ...formData, retrocessions: newRetrocessions });
    }
  };

  const handleCustomPercentageChange = (index: number, lineIndex: number, percentage: number) => {
    if (!readOnly) {
      const newRetrocessions = [...formData.retrocessions];
      const retrocession = newRetrocessions[index];

      if (retrocession.distribution_type !== 'custom') return;

      // On reconstruit la carte à partir des lignes actuelles : les clés obsolètes
      // (ancien format sans agence, négociateur retiré) ne sont pas conservées,
      // sinon elles faussaient le total.
      const currentValues = getDistributionValues(retrocession.distribution_percentages);
      const newPercentages = negotiatorKeys.reduce((acc, key, i) => {
        acc[key] = i === lineIndex ? percentage : currentValues[i];
        return acc;
      }, {} as { [key: string]: number });

      newPercentages.total = Object.values(newPercentages).reduce((sum, value) => sum + value, 0);

      retrocession.distribution_percentages = newPercentages as { [key: string]: number; total: number };

      onChange({ ...formData, retrocessions: newRetrocessions });
    }
  };

  const netFeesWithoutTax = useMemo(() => {
    const feesWithoutTax = parseFloat(formData.fees_without_tax);
    const totalRetrocessions = formData.retrocessions.reduce((sum, r) => {
      const amount = parseFloat(r.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    
    if (isNaN(feesWithoutTax)) return 0;
    return feesWithoutTax - totalRetrocessions;
  }, [formData.fees_without_tax, formData.retrocessions]);

  const getRetrocessionTypeLabel = (type: string) => {
    switch (type) {
      case 'champagne':
        return 'Champagne (21€)';
      case 'ambassadeur':
        return 'Ambassadeur (150€/200€/300€/350€/500€)';
      case 'diagnostic':
        return 'Diagnostic (Montant libre)';
      case 'recommandation_interne':
        return 'Recommandation interne (20% HT)';
      case 'uff':
        return 'UFF (20% HT)';
      case 'retrocession_lefeuvre':
        return 'Rétrocession Lefeuvre (20% HT)';
      case 'retrocession_newco':
        return 'Rétrocession Newco (20% HT)';
      case 'retrocession_syndic':
        return 'Rétrocession KS (20% HT)';
      case 'duo_coaching':
        return 'Duo Coaching (20% HT)';
      case 'intercabinet':
        return 'Intercabinet (Montant libre)';
      case 'geste_commercial':
        return 'Geste commercial (Montant libre)';
      case 'autre':
        return 'Autre (Montant libre)';
      case 'apporteur_affaire':
        return "Apporteur d'affaire (Montant libre)";
      default:
        return type;
    }
  };

  // Filtrer les négociateurs avec des IDs valides
  const validNegotiators = formData.negotiators.filter(n => n.id);

  // Une entrée de répartition par ligne de négociateur (négociateur + agence)
  const negotiatorKeys = buildDistributionLineKeys(validNegotiators);
  const negotiatorLabels = buildLineLabels(validNegotiators);

  /**
   * Valeurs affichées pour chaque ligne, selon la même règle que le calcul des
   * statistiques : une part propre « id|AGENCE » si elle existe, sinon la clé à
   * l'ancien format (ID seul) répartie entre les lignes du négociateur qui n'en
   * ont pas. Les deux vues affichent ainsi toujours les mêmes montants.
   */
  const getDistributionValues = (percentages?: { [key: string]: number }): number[] => {
    const ownShares = validNegotiators.map((line, i) => {
      const key = negotiatorKeys[i];
      // Une clé égale à l'ID seul relève de l'ancien format, traité juste après
      if (key === line.id) return null;
      const stored = percentages?.[key];
      return typeof stored === 'number' ? stored : null;
    });

    return ownShares.map((share, i) => {
      if (share !== null) return share;

      const line = validNegotiators[i];
      const legacy = getRowDistributionPercentage(percentages, line.id, null);
      if (!legacy) return 0;

      const sharedBy = validNegotiators.filter(
        (other, j) => other.id === line.id && ownShares[j] === null
      ).length;
      return sharedBy > 0 ? legacy / sharedBy : 0;
    });
  };

  // Signature de la liste des négociateurs de la vente (identités + agences)
  const negotiatorsSignature = negotiatorKeys.join('||');

  /**
   * Garde-fou : les négociateurs des rétrocessions doivent rester ceux de la vente.
   *
   * Quand un négociateur est remplacé après la saisie des rétrocessions, la
   * répartition continuait de désigner l'ancien : son montant n'était plus imputé
   * à personne et disparaissait des statistiques, sans rien signaler.
   *
   * On réaligne donc la répartition sur les négociateurs courants — les parts des
   * négociateurs retirés sont supprimées, les nouveaux entrent à 0 %, ce qui casse
   * volontairement le total et force une ressaisie explicite.
   */
  useEffect(() => {
    if (readOnly) return;

    const previous = previousSignatureRef.current;
    previousSignatureRef.current = negotiatorsSignature;

    // Premier rendu, ou chargement initial de la transaction : rien à réaligner
    if (!previous || previous === negotiatorsSignature) return;
    if (formData.retrocessions.length === 0) return;

    let hasChanges = false;
    const synced = formData.retrocessions.map(retrocession => {
      if (retrocession.distribution_type === 'custom') {
        const values = getDistributionValues(retrocession.distribution_percentages);
        const rebuilt = negotiatorKeys.reduce((acc, key, i) => {
          acc[key] = values[i];
          return acc;
        }, {} as { [key: string]: number });
        rebuilt.total = Object.values(rebuilt).reduce((sum, v) => sum + v, 0);

        hasChanges = true;
        return {
          ...retrocession,
          distribution_percentages: rebuilt as { [key: string]: number; total: number },
          primary_negotiator_id: validNegotiators[0]?.id || '',
          secondary_negotiator_id: validNegotiators[1]?.id || null,
          tertiary_negotiator_id: validNegotiators[2]?.id || null,
          quaternary_negotiator_id: validNegotiators[3]?.id || null
        };
      }

      // Distribution à bénéficiaire unique : il doit rester sur la vente
      const stillOnSale = validNegotiators.some(n => n.id === retrocession.primary_negotiator_id);
      if (stillOnSale) return retrocession;

      hasChanges = true;
      return { ...retrocession, primary_negotiator_id: '' };
    });

    if (hasChanges) {
      onChange({ ...formData, retrocessions: synced });
    }
    // Déclenché uniquement par un changement de négociateurs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiatorsSignature, readOnly]);

  // Anomalies bloquantes signalées à l'utilisateur (même règle qu'à l'enregistrement)
  const retrocessionProblems = checkRetrocessionsAgainstNegotiators(
    formData.retrocessions,
    validNegotiators
  );

  // Log initial des données
  console.log('[RetrocessionsForm] Current form data:', {
    negotiators: formData.negotiators,
    validNegotiators,
    retrocessions: formData.retrocessions.map(r => ({
      type: r.type,
      amount: r.amount,
      distribution_type: r.distribution_type,
      distribution_percentages: r.distribution_percentages,
      primary_negotiator_id: r.primary_negotiator_id,
      secondary_negotiator_id: r.secondary_negotiator_id,
      tertiary_negotiator_id: r.tertiary_negotiator_id,
      quaternary_negotiator_id: r.quaternary_negotiator_id
    }))
  });

  // Fonction pour obtenir le montant par défaut en fonction du type
  const getDefaultAmount = (type: string) => {
    const feesHT = parseFloat(formData.fees_without_tax);
    const twentyPercent = isNaN(feesHT) ? 0 : feesHT * 0.2;

    switch (type) {
      case 'champagne':
        return '21';
      case 'ambassadeur':
        return '150';
      case 'recommandation_interne':
      case 'uff':
      case 'retrocession_lefeuvre':
      case 'retrocession_newco':
      case 'retrocession_syndic':
        return twentyPercent.toFixed(2);
      case 'duo_coaching':
        return (twentyPercent / 1.2).toFixed(2);
      case 'apporteur_affaire':
        return '0';
      default:
        return '0';
    }
  };

  // Fonction pour vérifier si le montant est modifiable
  const isAmountEditable = (type: string) => {
    return ['diagnostic', 'intercabinet', 'geste_commercial', 'autre', 'apporteur_affaire'].includes(type);
  };

  // Fonction pour obtenir les options de montant pour l'ambassadeur
  const getAmbassadeurOptions = () => [
    { value: '150', label: '150 €' },
    { value: '200', label: '200 €' },
    { value: '300', label: '300 €' },
    { value: '350', label: '350 €' },
    { value: '500', label: '500 €' }
  ];

  const inputCls = "w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400/30 focus:border-rose-400 transition-colors bg-white disabled:bg-slate-50 disabled:text-slate-500";
  const labelCls = "block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5";

  return (
    <div className="space-y-3">

      {/* Garde-fou : cohérence entre les négociateurs de la vente et des rétrocessions */}
      {!readOnly && retrocessionProblems.length > 0 && (
        <div className="flex gap-3 p-3 rounded-xl border border-amber-300 bg-amber-50">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 min-w-0">
            <p className="font-semibold">Répartition à corriger avant d'enregistrer</p>
            <ul className="mt-1 space-y-0.5">
              {retrocessionProblems.map((problem, i) => (
                <li key={i} className="text-amber-800">• {problem}</li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-amber-700">
              Les négociateurs des rétrocessions doivent être ceux de la vente.
            </p>
          </div>
        </div>
      )}

      {/* Liste des rétrocessions */}
      {formData.retrocessions.map((retrocession, index) => {
        // Total recalculé sur les lignes affichées (le champ "total" stocké pouvait
        // ignorer un négociateur présent deux fois)
        const distributionValues = getDistributionValues(retrocession.distribution_percentages);
        const total = Math.round(distributionValues.reduce((sum, v) => sum + v, 0) * 100) / 100;
        const totalOk = total === 100;

        return (
          <div key={index} className="rounded-xl border border-rose-100 bg-rose-50/40 overflow-hidden">
            {/* En-tête de la carte */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-rose-50 border-b border-rose-100">
              <span className="text-xs font-semibold text-rose-600 uppercase tracking-wide">
                Rétrocession #{index + 1}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleRemoveRetrocession(index)}
                  className="w-6 h-6 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-500 hover:text-rose-700 flex items-center justify-center transition-colors"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Corps de la carte */}
            <div className="p-4 space-y-3">
              {/* Ligne 1 : Type + Montant */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select
                    value={retrocession.type}
                    onChange={(e) => {
                      if (!readOnly) {
                        const newType = e.target.value as FormData['retrocessions'][0]['type'];
                        const newAmount = getDefaultAmount(newType);
                        const newRetrocessions = [...formData.retrocessions];
                        newRetrocessions[index] = { ...newRetrocessions[index], type: newType, amount: newAmount };
                        onChange({ ...formData, retrocessions: newRetrocessions });
                      }
                    }}
                    className={inputCls}
                    required
                    disabled={readOnly}
                  >
                    {['champagne','ambassadeur','diagnostic','recommandation_interne','uff','retrocession_lefeuvre','retrocession_newco','retrocession_syndic','duo_coaching','intercabinet','geste_commercial','autre','apporteur_affaire'].map(type => (
                      <option key={type} value={type}>{getRetrocessionTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Montant</label>
                  {retrocession.type === 'ambassadeur' ? (
                    <select
                      value={retrocession.amount}
                      onChange={(e) => {
                        if (!readOnly) {
                          const newRetrocessions = [...formData.retrocessions];
                          newRetrocessions[index] = { ...newRetrocessions[index], amount: e.target.value };
                          onChange({ ...formData, retrocessions: newRetrocessions });
                        }
                      }}
                      className={inputCls}
                      required
                      disabled={readOnly}
                    >
                      {getAmbassadeurOptions().map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="relative">
                      <input
                        type="number"
                        value={retrocession.amount}
                        onChange={(e) => {
                          if (!readOnly && isAmountEditable(retrocession.type)) {
                            const newRetrocessions = [...formData.retrocessions];
                            newRetrocessions[index] = { ...newRetrocessions[index], amount: e.target.value };
                            onChange({ ...formData, retrocessions: newRetrocessions });
                          }
                        }}
                        className={`${inputCls} pr-8`}
                        min="0"
                        step="0.01"
                        required
                        readOnly={readOnly || !isAmountEditable(retrocession.type)}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">€</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ligne 2 : Description + Distribution */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Description</label>
                  <input
                    type="text"
                    value={retrocession.description || ''}
                    onChange={(e) => {
                      if (!readOnly) {
                        const newRetrocessions = [...formData.retrocessions];
                        newRetrocessions[index] = { ...newRetrocessions[index], description: e.target.value };
                        onChange({ ...formData, retrocessions: newRetrocessions });
                      }
                    }}
                    className={inputCls}
                    placeholder="Optionnel…"
                    readOnly={readOnly}
                  />
                </div>

                <div>
                  <label className={labelCls}>Distribution</label>
                  <select
                    value={retrocession.distribution_type === 'single' ? `single-${retrocession.primary_negotiator_id}` : 'custom'}
                    onChange={(e) => handleDistributionChange(index, e.target.value)}
                    className={inputCls}
                    disabled={readOnly || validNegotiators.length <= 1}
                  >
                    {validNegotiators.length === 1 ? (
                      <option value={`single-${validNegotiators[0].id}`}>100% {negotiatorLabels[0]}</option>
                    ) : (
                      <>
                        <option value="custom">Pourcentages personnalisés</option>
                        {validNegotiators
                          .map((n, i) => ({ n, i }))
                          // Un négociateur présent dans deux agences ne doit apparaître
                          // qu'une fois ici : "single" cible la personne, pas l'agence.
                          .filter(({ n, i }) => validNegotiators.findIndex(o => o.id === n.id) === i)
                          .map(({ n, i }) => (
                            <option key={negotiatorKeys[i]} value={`single-${n.id}`}>100% {n.name}</option>
                          ))}
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Pourcentages personnalisés */}
              {retrocession.distribution_type === 'custom' && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                  {validNegotiators.map((_negotiator, lineIndex) => {
                    const value = distributionValues[lineIndex];
                    return (
                      <div key={negotiatorKeys[lineIndex]} className="flex items-center gap-3">
                        <div className="relative w-24 shrink-0">
                          <input
                            type="number"
                            value={value}
                            onChange={(e) => handleCustomPercentageChange(index, lineIndex, parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 pr-7 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/30 focus:border-rose-400 transition-colors"
                            min="0"
                            max="100"
                            step="1"
                            disabled={readOnly}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">%</span>
                        </div>
                        <span className="text-sm font-medium text-slate-700 truncate" title={negotiatorLabels[lineIndex]}>
                          {negotiatorLabels[lineIndex]}
                        </span>
                      </div>
                    );
                  })}
                  {/* Total */}
                  <div className={`flex items-center justify-between pt-2 border-t text-xs font-semibold ${totalOk ? 'border-emerald-200 text-emerald-600' : 'border-red-200 text-red-500'}`}>
                    <span>Total</span>
                    <span className={`px-2 py-0.5 rounded-full ${totalOk ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      {total}% {!totalOk && '· doit être 100%'}
                    </span>
                  </div>
                </div>
              )}

              {/* Date de paiement (optionnel) */}
              {showPaymentDates && (
                <div>
                  <label className={labelCls}>Date de paiement</label>
                  <input
                    type="date"
                    value={retrocession.payment_date || ''}
                    onChange={(e) => {
                      if (!readOnly) {
                        const newRetrocessions = [...formData.retrocessions];
                        newRetrocessions[index] = { ...newRetrocessions[index], payment_date: e.target.value || null };
                        onChange({ ...formData, retrocessions: newRetrocessions });
                      }
                    }}
                    className={inputCls}
                    readOnly={readOnly}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Bouton ajouter */}
      {!readOnly && (
        <button
          type="button"
          onClick={handleAddRetrocession}
          disabled={validNegotiators.length === 0}
          className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-rose-200 rounded-xl text-sm font-medium text-rose-500 hover:border-rose-400 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus size={15} />
          Ajouter une rétrocession
        </button>
      )}

      {/* Honoraires Net HT */}
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Euro size={15} className="text-teal-500" />
          Honoraires Net HT
        </div>
        <span className="text-base font-bold text-slate-900">
          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(netFeesWithoutTax)}
        </span>
      </div>
    </div>
  );
}